/**
 * Direct asset uploads to the asset library (Pending Build item 16 "Asset uploads", R2 upload screen). Owner: delivery.
 * Mounted under /v1/deliverables/assets/uploads (+ GET /v1/deliverables/assets/usage, DELETE /v1/deliverables/assets/:id).
 * Gated by the feature flag ASSET_UPLOADS_FLAG (off unless a flag row enables it). Complements the cloud drive import
 * (assets.cloud_import, @qm/contracts/connectors-drive): both land in the same studio_assets library, go through the
 * same quarantine + scanner hook and are served by the same authorised Worker download route.
 *
 * Flow: POST /uploads (declare file; type, size, plan cap and quota checked; Idempotency-Key)
 *   -> PUT /uploads/:id/content (single streamed PUT, <= SINGLE_PUT_MAX_BYTES) or PUT /uploads/:id/parts/:n (multipart)
 *   -> POST /uploads/:id/complete (finalize: checksum, content sniffing, dimensions, SVG safety, pending_scan -> scan)
 *   -> ready | quarantined (malware) | rejected (content/type/size).
 * This file is shared by qm-api and the portal (client-side pre-checks use the same allowlist and limits).
 */
import { z } from 'zod';
import type { AssetKind } from './deliverables';

export const ASSET_UPLOADS_FLAG = 'assets.uploads';

const MB = 1024 * 1024;
const GB = 1024 * MB;

export interface UploadType { kind: AssetKind; extensions: readonly string[]; maxBytes: number }

/**
 * The upload allowlist: MIME -> kind, accepted extensions and the per-type size cap. Anything else is refused.
 * SVG is accepted only when it passes the strict safety check at finalize (no script, event handlers, external
 * references, foreignObject or entities); an unsafe SVG is rejected, never rewritten. Office documents must be
 * OOXML without macros (docm/xlsm/pptm and any vbaProject part are refused).
 */
export const UPLOAD_TYPES: Readonly<Record<string, UploadType>> = {
  'image/png': { kind: 'image', extensions: ['png'], maxBytes: 25 * MB },
  'image/jpeg': { kind: 'image', extensions: ['jpg', 'jpeg'], maxBytes: 25 * MB },
  'image/webp': { kind: 'image', extensions: ['webp'], maxBytes: 25 * MB },
  'image/gif': { kind: 'image', extensions: ['gif'], maxBytes: 25 * MB },
  'image/svg+xml': { kind: 'image', extensions: ['svg'], maxBytes: 2 * MB },
  'application/pdf': { kind: 'document', extensions: ['pdf'], maxBytes: 50 * MB },
  'video/mp4': { kind: 'video', extensions: ['mp4', 'm4v'], maxBytes: 2 * GB },
  'video/quicktime': { kind: 'video', extensions: ['mov'], maxBytes: 2 * GB },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { kind: 'document', extensions: ['docx'], maxBytes: 50 * MB },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { kind: 'document', extensions: ['xlsx'], maxBytes: 50 * MB },
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': { kind: 'document', extensions: ['pptx'], maxBytes: 50 * MB },
};
export const UPLOAD_MIMES = Object.keys(UPLOAD_TYPES);
/** `accept` attribute for the file picker. */
export const UPLOAD_ACCEPT = [...UPLOAD_MIMES, ...Object.values(UPLOAD_TYPES).flatMap((t) => t.extensions.map((e) => `.${e}`))].join(',');

/** Files up to this size go in one streamed PUT; larger files use multipart (parts of MULTIPART_PART_BYTES). */
export const SINGLE_PUT_MAX_BYTES = 50 * MB;
/** 16 MB parts: well under the edge request-body limit, above R2's 5 MB minimum part size. */
export const MULTIPART_PART_BYTES = 16 * MB;
export const MULTIPART_MAX_PARTS = 10_000;
/** An upload session that is not completed within this window expires and its reservation is released. */
export const UPLOAD_SESSION_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Per-plan limits. The plan is the tenant's live paid plan entitlement (billing_entitlements plan_* on an active or
 * past_due subscription); no plan -> 'none'. maxFileBytes caps one file (the per-type cap still applies);
 * quotaBytes caps the library's stored bytes (live versions of assets not deleted + open upload reservations).
 * OWNER TO CONFIRM (owner / Finance): every figure below is an engineering placeholder, not an approved commercial
 * limit. See docs/status/asset-uploads.md. Change them here only; the API and the portal both read this table.
 */
export const UPLOAD_PLAN_TIERS = ['none', 'starter', 'growth', 'pipeline', 'managed', 'enterprise'] as const;
export type UploadPlanTier = (typeof UPLOAD_PLAN_TIERS)[number];
export const UPLOAD_PLAN_LIMITS: Readonly<Record<UploadPlanTier, { maxFileBytes: number; quotaBytes: number }>> = {
  none: { maxFileBytes: 25 * MB, quotaBytes: 1 * GB },
  starter: { maxFileBytes: 100 * MB, quotaBytes: 10 * GB },
  growth: { maxFileBytes: 500 * MB, quotaBytes: 50 * GB },
  pipeline: { maxFileBytes: 1 * GB, quotaBytes: 100 * GB },
  managed: { maxFileBytes: 2 * GB, quotaBytes: 250 * GB },
  enterprise: { maxFileBytes: 2 * GB, quotaBytes: 1024 * GB },
};
/** plan_<tier> SKU -> tier (billing catalogue SKUs). */
export const planTierForSku = (sku: string): UploadPlanTier | null => {
  const t = sku.startsWith('plan_') ? sku.slice(5) : '';
  return (UPLOAD_PLAN_TIERS as readonly string[]).includes(t) && t !== 'none' ? (t as UploadPlanTier) : null;
};

export const UPLOAD_REJECT_REASONS = [
  'unsupported_type', 'extension_mismatch', 'too_large', 'plan_limit', 'quota_exceeded', 'size_mismatch', 'checksum_mismatch',
  'content_mismatch', 'executable', 'unknown_content', 'unsafe_svg', 'office_macro', 'malware_detected', 'empty_file',
] as const;
export type UploadRejectReason = (typeof UPLOAD_REJECT_REASONS)[number];

export const extensionOf = (filename: string): string => {
  const i = filename.lastIndexOf('.');
  return i >= 0 ? filename.slice(i + 1).toLowerCase() : '';
};
/** MIME for a filename from the allowlist (browsers leave File.type empty for some Office files). */
export const mimeForFilename = (filename: string): string | null => {
  const ext = extensionOf(filename);
  return UPLOAD_MIMES.find((m) => UPLOAD_TYPES[m]!.extensions.includes(ext)) ?? null;
};

/** Type and size check for a declared file (API at create time; portal before it asks). */
export function uploadCheck(input: { filename: string; mime: string; sizeBytes: number }, maxFileBytes: number):
  { ok: true; kind: AssetKind; mime: string; maxBytes: number } | { ok: false; reason: UploadRejectReason; maxBytes?: number } {
  const mime = (input.mime || mimeForFilename(input.filename) || '').toLowerCase();
  const t = UPLOAD_TYPES[mime];
  if (!t) return { ok: false, reason: 'unsupported_type' };
  if (!t.extensions.includes(extensionOf(input.filename))) return { ok: false, reason: 'extension_mismatch' };
  if (input.sizeBytes <= 0) return { ok: false, reason: 'empty_file' };
  if (input.sizeBytes > t.maxBytes) return { ok: false, reason: 'too_large', maxBytes: t.maxBytes };
  if (input.sizeBytes > maxFileBytes) return { ok: false, reason: 'plan_limit', maxBytes: maxFileBytes };
  return { ok: true, kind: t.kind, mime, maxBytes: Math.min(t.maxBytes, maxFileBytes) };
}

const TagSchema = z.string().trim().min(1).max(40).regex(/^[\p{L}\p{N} _-]+$/u);
export const CreateUploadSchema = z.object({
  filename: z.string().trim().min(1).max(255).refine((s) => ![...s].some((ch) => ch === '/' || ch === '\\' || ch.charCodeAt(0) < 0x20), 'invalid_filename'),
  mime: z.string().trim().max(100).default(''),
  sizeBytes: z.number().int().positive(),
  title: z.string().trim().min(1).max(200).optional(),
  tags: z.array(TagSchema).max(30).default([]),
  /** Optional SHA-256 (hex) computed by the client; verified at finalize for single PUT uploads. */
  sha256: z.string().regex(/^[a-f0-9]{64}$/).optional(),
});
export type CreateUpload = z.infer<typeof CreateUploadSchema>;

export const CompleteUploadSchema = z.object({
  /** Multipart only: the parts the client uploaded (number + etag from each part response). */
  parts: z.array(z.object({ partNumber: z.number().int().min(1).max(MULTIPART_MAX_PARTS), etag: z.string().min(1).max(200) })).max(MULTIPART_MAX_PARTS).optional(),
}).default({});

export const UPLOAD_STATUSES = ['awaiting_upload', 'uploading', 'pending_scan', 'ready', 'quarantined', 'rejected', 'aborted', 'expired'] as const;
export type UploadStatus = (typeof UPLOAD_STATUSES)[number];

export interface UploadView {
  id: string; assetId: string; version: number; filename: string; mime: string; kind: AssetKind; sizeBytes: number;
  mode: 'single' | 'multipart'; partSize: number | null; partCount: number | null; uploadedParts: number[];
  status: UploadStatus; reason: UploadRejectReason | null; checksum: string | null; width: number | null; height: number | null;
  /** PUT path for a single upload, or the part path template (replace {n}) for multipart. */
  uploadPath: string; completePath: string; expiresAt: string; createdAt: string;
}

export interface UploadUsageView {
  plan: UploadPlanTier; usedBytes: number; reservedBytes: number; quotaBytes: number; maxFileBytes: number;
  singlePutMaxBytes: number; partBytes: number; types: { mime: string; kind: AssetKind; extensions: readonly string[]; maxBytes: number }[];
}

/** Queue job (SYSTEM_QUEUE, handled in qm-api through the jobs bridge): delete a soft-deleted asset's bytes from R2. */
export const ASSET_PURGE_JOB = 'deliverables.asset.purge';

/**
 * Queue job (SYSTEM_QUEUE -> qm-jobs -> qm-api jobs bridge): scan one quarantined asset version (size, content sniff,
 * AV provider hook) and mark it available or rejected. Queued by every ingest path when no inline scanner answered.
 */
export const ASSET_SCAN_JOB = 'deliverables.asset.scan';
/** The scan consumer runs only for tenants with this flag on (default off: versions stay quarantined, as before). */
export const ASSET_SCAN_FLAG = 'assets.scan_consumer';
/** qm-api bridge task (qm-jobs every-15-minutes cron): re-queue quarantined versions that never got a verdict. */
export const ASSET_SCAN_SWEEP_TASK = 'deliverables.assets.scan-sweep';
